<template>
  <div class="medias-page container">
    <TheBreadcrumb :pages="arianeRoute" />
    <HeaderCommunity :page="currentPageInfo" />
    <SearchResults
      :is-loading="isLoading"
      placeholder-text="Rechercher une avis média"
      @search-update="searchUpdateHandler"
    >
      <template #results>
        <CardContainer
          :items="communityMedias"
          :no-result-text="noContent"
        />
      </template>
    </SearchResults>
    <ThePagination
      :total-pages="communityMediasPages"
      :current-page="communityMediasCurrentPage"
      :go-to="changePageTo"
    />
    <PegiWarning v-if="community?.pegi === '18'" />
  </div>
</template>

<script setup lang="ts">
definePageMeta({
  middleware: async (to) => {
    const loadMediasReviewsCommunity = useLoadMediasReviewsCommunity()
    await loadMediasReviewsCommunity(to)
  },
})

const mediasStore = useMediasStore()
const { communityMedias, communityMediasPages, communityMediasCurrentPage } =
  storeToRefs(mediasStore)

const communitiesStore = useCommunitiesStore()
const { community } = storeToRefs(communitiesStore)

const arianeRoute = computed(() => {
  return [
    {
      title: 'Toutes les communautés',
      route: '/communautes',
    },
    {
      title: community.value?.title,
      route: `/communautes/${community.value?.slug}`,
    },
    {
      title: "L'avis des médias",
      route: ``,
    },
  ]
})

const breadcrumbSchema = useBreadcrumbSchema({
  arianeRoute,
})

const link = useLinkPrevNext({
  currentPage: communityMediasCurrentPage,
  pagesCount: communityMediasPages,
})

useHead({
  title: `Avis médias et presse ${community.value?.title}`,
  link,
  script: [breadcrumbSchema],
})

useSeoMeta({
  title: `Avis médias et presse ${community.value?.title}`,
  description: `Envie de connaître l'avis de la presse sur ${community.value?.title} ? Découvrez tous les avis de la presse à propos de ${community.value?.title} sur We Are PlayStation.`,
  ogTitle: `Tous les avis médias et presse sur ${community.value?.title}.`,
  ogDescription: `Découvrez tous les avis de la presse à propos de ${community.value?.title}.`,
  ogImage:
    community.value?.image_desktop?.url ??
    'https://www.weareplaystation.fr/img/og-img.jpg',
})
const referentialStore = useReferentialStore()
const { referential } = storeToRefs(referentialStore)

const mediaQuery = useCustomMediaQuery()

const currentPageInfo = computed(() => {
  const image = getImage(community, mediaQuery)

  return {
    title: "L'avis des médias",
    game: community.value?.title,
    pegi: community.value?.pegi,
    members: community.value?.users_count,
    image,
    contribPoints: referential.value?.points.post_source_create,
    description: `Découvrez tous les avis de la presse à propos de ${community.value?.title}.`,
  }
})

const route = useRoute()

const slug = computed(() => {
  return route.params.communityName as string
})

const searchData = async () => {
  isLoading.value = true

  await mediasStore.fetchCommunityMedias({
    page: 1,
    community: slug.value,
    search: search.value,
  })

  if (!communityMedias.value.length) {
    noContent.value = !search.value
      ? 'Aucun contenu'
      : 'Aucun résultat pour votre recherche'
  }

  isLoading.value = false
}

const { isLoading, search, searchUpdateHandler, noContent } = useSearching({
  searchData,
})

const changePageTo = async (index: number) => {
  if (index > 0) {
    mediasStore.fetchCommunityMedias({
      page: index,
      community: slug.value,
      search: search.value,
    })
  }
}
</script>

<style lang="scss" scoped>
.medias-page {
  min-height: 100vh;
  padding-bottom: 10rem;
}
</style>
